import { createFileRoute } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/PageShell";
import { BlogPostGrid } from "@/components/site/BlogPostGrid";
import { seoMeta, canonicalLink, absoluteUrl, robotsMeta } from "@/lib/seo";
import { SITE_URL } from "@/lib/site";
import { getPublishedPosts } from "@/server/blog/blog-public.fns";

const DEFAULT_TITLE = "Blog BTL Lima: Activaciones y Trade Marketing | Improve";
const DEFAULT_DESCRIPTION =
  "Guías y artículos prácticos sobre activaciones BTL, eventos corporativos, trade marketing, impulsación, sampling y volanteo publicitario en Lima.";

export const Route = createFileRoute("/blog/")({
  // `q` opcional: con un valor por defecto, el router normalizaba `/blog` a `/blog?q=` con un 307 y
  // todos los enlaces internos apuntaban a esa URL no canónica.
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === "string" && search.q.trim() !== "" ? { q: search.q } : {},
  loader: () => getPublishedPosts(),
  component: Blog,
  head: ({ loaderData, match }) => {
    const posts = loaderData ?? [];
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/blog",
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: canonicalLink(override?.canonicalPath ?? "/blog"),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "@id": absoluteUrl("/blog"),
            name: "Blog Improve Yourself",
            description:
              "Contenido sobre activaciones BTL, eventos corporativos y trade marketing en Lima.",
            url: absoluteUrl("/blog"),
            inLanguage: "es-PE",
            publisher: {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: "Improve Yourself",
            },
            blogPost: posts.map((p) => ({
              "@type": "BlogPosting",
              headline: p.h1,
              description: p.metaDescription,
              url: absoluteUrl(`/blog/${p.slug}`),
              datePublished: p.publishedAt,
              inLanguage: "es-PE",
            })),
          }),
        },
      ],
    };
  },
});

function Blog() {
  const { q = "" } = Route.useSearch();
  const posts = Route.useLoaderData() ?? [];
  return (
    <PageShell>
      <PageHero
        eyebrow="· Blog BTL"
        h1="Blog BTL: activaciones, eventos y trade marketing en Lima"
        intro="Guías prácticas para áreas de marketing y marcas que invierten en activaciones BTL, eventos corporativos, trade marketing e impulsación en Lima."
      />
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <BlogPostGrid posts={posts} initialQuery={q} />
        </div>
      </section>
    </PageShell>
  );
}
